import { useCallback, useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { useProductRows } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/hooks/useProductRows';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useFormatEventDate } from '@localization/hooks/useFormatEventDate';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import type { BurgerMenuProps } from './components/BurgerMenu/BurgerMenu';
import { useGetUniversalBurgerMenuDataQuery } from './UniversalBurgerMenu.graphql.generated';
import { extractHeaderLayoutPhoto, resolvePhotoUrl, useExtractEventPhoto } from '@shared/utils/eventPhoto';

export const GLOBAL_NAV_MENU = 'GlobalNavMenu';

export function useUniversalBurgerMenuController() {
  const { provideLogout } = useAuth();
  const telemetry = useAdminRegistryTelemetry();
  const { eventName } = useEventNameMatch();
  const { data } = useGetUniversalBurgerMenuDataQuery({
    variables: {
      eventHandle: eventName || '',
    },
    batchMode: 'fast',
  });

  const eventPhoto = useExtractEventPhoto(data?.eventByName);
  const headerLayoutPhoto = extractHeaderLayoutPhoto(data?.eventByName);

  const productRows = useProductRows(
    data?.eventByName?.pages ?? [],
    data?.eventByName?.productVerticals ?? {},
    data?.eventByName?.id,
    undefined,
    data?.eventByName?.pageContainers ?? [],
  );

  const { tq } = useTranslation('adminDashboardV3');

  // Resolves the date in the event's timezone. The previous `format(parseISO(timestamp), …)` resolved
  // it in the *viewer's* timezone, so an event whose local date differs from its UTC date showed the
  // wrong day. Also prefers `finalizedDate`, which this menu skipped entirely.
  const eventDate = useFormatEventDate(data?.eventByName?.info, 'MMMM dd, yyyy', { useTentativeFallback: true });

  const trackAndProvideLogout = useCallback(() => {
    telemetry.trackRegistryNavMenuItemClicked('accountSignOut');
    provideLogout({});
  }, [telemetry, provideLogout]);

  const items: BurgerMenuProps['items'] = useMemo(() => {
    const ret: BurgerMenuProps['items'] = productRows.map(row => {
      return {
        headerText: row.title,
        items: row.children.flatMap(rowChild => [
          {
            text: rowChild.title,
            icon: rowChild.icon,
            onClick: () => rowChild?.onClick?.(GLOBAL_NAV_MENU),
            newPillText: rowChild.hasNewPill ? tq`New` : undefined,
            notificationPill: rowChild.notificationPill,
            isDisabled: rowChild.isDisabled,
            pageVisibility: rowChild.pageVisibility,
            containerVisibility: rowChild.containerVisibility,
            path: rowChild.path,
            pathIsInJoyWeb: rowChild.pathIsInJoyWeb,
            subItems: rowChild.subItems && [
              {
                headerText: rowChild.title,
                items: rowChild.subItems?.map(subItem => {
                  return {
                    text: subItem.customTitle || subItem.title,
                    icon: subItem.icon,
                    newPillText: subItem.hasNewPill ? tq`New` : undefined,
                    // Surface the messaging Drafts/Scheduled counts as a neutral mono14 counter badge so
                    // the hamburger stays in sync with the dashboard flyout — failed-scheduled shows the
                    // red warning pill. (The orange notificationPill is reserved for attention counts like
                    // registry pending refunds.)
                    count: subItem.count,
                    countIsWarning: subItem.countIsWarning,
                    onClick: () => subItem?.onClick?.(GLOBAL_NAV_MENU),
                    isDisabled: subItem.isDisabled,
                    pageVisibility: subItem.pageVisibility,
                    containerVisibility: subItem.containerVisibility,
                    path: subItem.path,
                    // The sub-item's own flag, not the parent's: a legacy parent (e.g. Guest List →
                    // /edit/guests) can hold joy-web SPA sub-items (Seating Chart, RSVP Tracking).
                    pathIsInJoyWeb: subItem.pathIsInJoyWeb,
                    topDivider: subItem.hasDivider,
                  };
                }),
              },
            ],
          },
        ]),
      };
    });
    return ret;
  }, [productRows, tq]);

  return {
    items,
    trackAndProvideLogout,
    eventDate,
    location: data?.eventByName?.info.location,
    photoUrl: resolvePhotoUrl(eventPhoto, headerLayoutPhoto, '') || data?.eventByName?.photo?.url,
    eventId: data?.eventByName?.id,
    eventHandle: eventName,
  };
}
